/**
 * The announcement body's small Markdown subset (TMMPDM-0110; TMMAPI-0181 item 32a: "the same small
 * Markdown subset the help library uses"), parsed into a tiny tree the preview renders with React
 * elements. No library and no HTML string: React escapes every text node, so raw HTML in a body is
 * shown as the characters typed, never interpreted.
 *
 * The subset:
 * - `#`, `##`, `###` headings;
 * - paragraphs (lines joined; a blank line starts a new one);
 * - `-` / `*` bullet lists and `1.` numbered lists;
 * - `**bold**`, `*italic*` / `_italic_` and `` `code` ``;
 * - `[text](url)` links, only to `https:`, `http:` or `mailto:` (anything else shows its text);
 * - an image `![alt](src)` shows its alt text only: nothing is ever loaded from a body.
 * Anything else is text.
 */

export type MarkdownInline =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: MarkdownInline[] }
  | { type: 'em'; children: MarkdownInline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: MarkdownInline[] };

export type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3; children: MarkdownInline[] }
  | { type: 'paragraph'; children: MarkdownInline[] }
  | { type: 'list'; ordered: boolean; items: MarkdownInline[][] };

const SAFE_HREF = /^(?:https?:\/\/|mailto:)/i;

/** A link's target when it is one the preview may link to; otherwise null (its text shows). */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  return SAFE_HREF.test(href) ? href : null;
}

interface InlineRule {
  /** Global (`g`): matched from an offset with `lastIndex`, never on a re-sliced string. */
  pattern: RegExp;
  build: (match: RegExpExecArray, allowLinks: boolean) => MarkdownInline;
}

/*
 * Every span is bounded (at most 500 characters of marked text, 2,000 of link target, one line), so
 * an unclosed `[`, `**` or backtick can't make one search scan to the end of the body from every
 * position (TMMPDM-0110 QA: unbounded, an 8,000-character body took seconds).
 */
const INLINE_RULES: InlineRule[] = [
  // An image is its alt text: nothing is fetched or rendered from a body.
  {
    pattern: /!\[([^\]\n]{0,500})\]\(([^)\n]{0,2000})\)/g,
    build: (m) => ({ type: 'text', text: m[1] ?? '' }),
  },
  {
    pattern: /\[([^\]\n]{1,500})\]\(([^)\s]{1,2000})\)/g,
    build: (m, allowLinks) => {
      const href = allowLinks ? safeHref(m[2] ?? '') : null;
      // A link the preview may not follow (or one inside a link) shows its text alone.
      return href === null
        ? { type: 'text', text: m[1] ?? '' }
        : { type: 'link', href, children: parseInline(m[1] ?? '', false) };
    },
  },
  { pattern: /`([^`\n]{1,500})`/g, build: (m) => ({ type: 'code', text: m[1] ?? '' }) },
  {
    pattern: /\*\*([^\n]{1,500}?)\*\*/g,
    build: (m, allowLinks) => ({ type: 'strong', children: parseInline(m[1] ?? '', allowLinks) }),
  },
  {
    pattern: /\*([^*\s][^*\n]{0,500}?)\*|_([^_\s][^_\n]{0,500}?)_/g,
    build: (m, allowLinks) => ({
      type: 'em',
      children: parseInline(m[1] ?? m[2] ?? '', allowLinks),
    }),
  },
];

/**
 * One line's inline marks; unmatched marks are text. Each rule's next match is found once and kept
 * until the text before it is consumed; a rule with no match left is dropped for the rest of the
 * line. So the line is searched about once per rule, not once per rule per mark.
 */
export function parseInline(source: string, allowLinks = true): MarkdownInline[] {
  const out: MarkdownInline[] = [];
  // undefined: not searched yet; null: no match left in this line.
  const next: Array<RegExpExecArray | null | undefined> = INLINE_RULES.map(() => undefined);
  let offset = 0;
  while (offset < source.length) {
    let best = -1;
    for (let i = 0; i < INLINE_RULES.length; i += 1) {
      let match = next[i];
      if (match === null) continue;
      if (match === undefined || match.index < offset) {
        const pattern = (INLINE_RULES[i] as InlineRule).pattern;
        pattern.lastIndex = offset;
        match = pattern.exec(source);
        next[i] = match;
        if (match === null) continue;
      }
      const current = best === -1 ? null : next[best];
      if (current == null || match.index < current.index) best = i;
    }
    const match = best === -1 ? null : next[best];
    if (match == null) {
      pushText(out, source.slice(offset));
      break;
    }
    if (match.index > offset) pushText(out, source.slice(offset, match.index));
    out.push((INLINE_RULES[best] as InlineRule).build(match, allowLinks));
    offset = match.index + match[0].length;
  }
  return out;
}

function pushText(out: MarkdownInline[], text: string) {
  const last = out[out.length - 1];
  if (last?.type === 'text') last.text += text;
  else out.push({ type: 'text', text });
}

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;
const NUMBERED = /^\d{1,9}[.)]\s+(.*)$/;

/** The body as blocks. */
export function parseMarkdownSubset(source: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', children: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list !== null) {
      blocks.push({
        type: 'list',
        ordered: list.ordered,
        items: list.items.map((item) => parseInline(item)),
      });
      list = null;
    }
  };

  for (const raw of source.split(/\r?\n/)) {
    const line = raw.trim();
    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = (heading[1] ?? '#').length as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, children: parseInline(heading[2] ?? '') });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = numbered !== null;
      if (list !== null && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((bullet ?? numbered)?.[1] ?? '');
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}
